import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Mail, Users, TrendingUp, RefreshCw, Eye, Send,
  MessageSquare, Target, Calendar, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { API } from "@/lib/constants";

const EmailCampaignDashboard = () => {
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [enrollments, setEnrollments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, statsRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/email-campaigns/categories`),
        axios.get(`${API}/email-campaigns/stats`),
        axios.get(`${API}/email-campaigns/enrollments?limit=50`)
      ]);
      
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      console.error("Error fetching campaign data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePreview = async (category) => {
    try {
      const response = await axios.get(`${API}/email-campaigns/preview/${encodeURIComponent(category)}?first_name=John`);
      setPreviewContent({ category, ...response.data });
      setSelectedCategory(category);
    } catch (error) {
      console.error("Error fetching preview:", error);
    }
  };

  const handleSetupProperties = async () => {
    try {
      await axios.post(`${API}/admin/hubspot/setup-category-properties`);
      alert("HubSpot category properties created successfully!");
    } catch (error) {
      alert("Failed to create properties: " + error.message);
    }
  };

  const getTotalEnrollments = () => Object.values(stats).reduce((sum, s) => sum + (s.total_enrollments || 0), 0);
  const getActiveEnrollments = () => Object.values(stats).reduce((sum, s) => sum + (s.active || 0), 0);
  const getTotalEmailsSent = () => Object.values(stats).reduce((sum, s) => sum + (s.total_emails_sent || 0), 0);

  const categoryColors = {
    "Artificial Intelligence": "#00E676",
    "Social Media": "#6366F1",
    "Video Marketing": "#F59E0B",
    "Digital Platform": "#EC4899",
    "B2B Marketing": "#14B8A6",
    "Generative Engine Optimization": "#8B5CF6",
    "default": "#64748B"
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-[#00E676] animate-pulse">Loading campaign data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-black" data-testid="email-campaign-dashboard">
      <div className="container-custom">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Email Campaign Dashboard</h1>
            <p className="text-gray-400">
              Category-based email nurture sequences for blog readers
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={handleSetupProperties} className="btn-outline">
              Setup HubSpot Properties
            </Button>
            <Button onClick={fetchData} className="btn-ninja" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <SummaryCard 
            icon={Users} 
            label="Total Enrollments" 
            value={getTotalEnrollments()} 
            color="#00E676" 
          />
          <SummaryCard 
            icon={Target} 
            label="Active Sequences" 
            value={getActiveEnrollments()} 
            color="#6366F1" 
          />
          <SummaryCard 
            icon={Send} 
            label="Emails Sent" 
            value={getTotalEmailsSent()} 
            color="#F59E0B" 
          />
          <SummaryCard 
            icon={Mail} 
            label="Campaign Types" 
            value={categories.length} 
            color="#EC4899" 
          />
        </div>

        {/* Campaign Categories */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#00E676]" />
                Email Campaigns by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {categories.map((cat) => {
                  const catStats = stats[cat.category] || { total_enrollments: 0, active: 0 };
                  const color = categoryColors[cat.category] || categoryColors.default;
                  
                  return (
                    <div 
                      key={cat.category}
                      className="p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all cursor-pointer"
                      onClick={() => handlePreview(cat.category)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: color }}
                          />
                          <span className="font-medium">{cat.category}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>{catStats.total_enrollments} enrolled</span>
                          <span>{catStats.active} active</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        Sequence: {cat.sequence_name} • Followup: {cat.followup_delay_days} days
                      </div>
                      <div className="text-sm text-gray-400 mt-1 truncate">
                        Subject: "{cat.email_subject}"
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Email Preview */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#00E676]" />
                Email Preview
                {selectedCategory && (
                  <span className="text-sm font-normal text-gray-400 ml-2">
                    - {selectedCategory}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {previewContent ? (
                <Accordion type="single" collapsible defaultValue="initial">
                  <AccordionItem value="initial" className="border-white/10">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#00E676]" />
                        Initial Email
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="p-4 bg-white/5 rounded-lg">
                        <div className="text-sm text-gray-400 mb-2">
                          Subject: <span className="text-white">{previewContent.initial_email.subject}</span>
                        </div>
                        <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">
                          {previewContent.initial_email.content}
                        </pre>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="followup" className="border-white/10">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#F59E0B]" />
                        Followup Email ({previewContent.followup_email.delay_days} days later)
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="p-4 bg-white/5 rounded-lg">
                        <div className="text-sm text-gray-400 mb-2">
                          Subject: <span className="text-white">{previewContent.followup_email.subject}</span>
                        </div>
                        <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">
                          {previewContent.followup_email.content}
                        </pre>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Click on a campaign category to preview emails</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Enrollments */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#00E676]" />
              Recent Enrollments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {enrollments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3 text-gray-400">Email</th>
                      <th className="text-left p-3 text-gray-400">Name</th>
                      <th className="text-left p-3 text-gray-400">Category</th>
                      <th className="text-left p-3 text-gray-400">Blog</th>
                      <th className="text-left p-3 text-gray-400">Enrolled</th>
                      <th className="text-left p-3 text-gray-400">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.slice(0, 10).map((enrollment, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 text-gray-300">{enrollment.email}</td>
                        <td className="p-3 text-gray-300">{enrollment.first_name}</td>
                        <td className="p-3">
                          <span 
                            className="px-2 py-1 rounded text-xs"
                            style={{ 
                              backgroundColor: `${categoryColors[enrollment.category] || categoryColors.default}20`,
                              color: categoryColors[enrollment.category] || categoryColors.default
                            }}
                          >
                            {enrollment.category}
                          </span>
                        </td>
                        <td className="p-3 text-gray-400 max-w-xs truncate">
                          {enrollment.blog_title || "-"}
                        </td>
                        <td className="p-3 text-gray-400">
                          {new Date(enrollment.enrolled_at).toLocaleDateString()}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            enrollment.status === 'active' 
                              ? 'bg-[#00E676]/20 text-[#00E676]' 
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {enrollment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No enrollments yet. Visitors will be enrolled when they engage with blog popups.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="bg-[#0A0A0A] border-white/10 mt-8">
          <CardHeader>
            <CardTitle>How Category Email Campaigns Work</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#00E676]/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#00E676] font-bold">1</span>
                </div>
                <h3 className="font-medium mb-2">User Reads Blog</h3>
                <p className="text-sm text-gray-400">Visitor reads a blog post in a specific category</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#6366F1]/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#6366F1] font-bold">2</span>
                </div>
                <h3 className="font-medium mb-2">Popup Appears</h3>
                <p className="text-sm text-gray-400">Contextual popup with category-matched offer</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#F59E0B]/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#F59E0B] font-bold">3</span>
                </div>
                <h3 className="font-medium mb-2">User Submits</h3>
                <p className="text-sm text-gray-400">Email captured and synced to HubSpot</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#EC4899]/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#EC4899] font-bold">4</span>
                </div>
                <h3 className="font-medium mb-2">Email Sequence</h3>
                <p className="text-sm text-gray-400">Automatic nurture emails based on category</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-[#0A0A0A] border-white/10">
    <CardContent className="p-6">
      <div className="flex items-center gap-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default EmailCampaignDashboard;
